import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  Stack,
  Button,
  Slider,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Scatter,
  ScatterChart,
  ZAxis,
  ComposedChart,
  Area
} from 'recharts';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import TuneIcon from '@mui/icons-material/Tune';
// import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import WarningIcon from '@mui/icons-material/Warning';
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import CustomerDetailDialog from '../../components/dialogs/CustomerDetailDialog';

// Renk paleti tanımlanması gereken yer
const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

// Zaman aralığı seçenekleri
const TIME_RANGES = [
  { value: '3m', label: 'Son 3 Ay' },
  { value: '6m', label: 'Son 6 Ay' },
  { value: '1y', label: 'Son 1 Yıl' },
  { value: '2y', label: 'Son 2 Yıl' }
];

// Churn tanımı ve hesaplama parametreleri
const CHURN_DEFINITIONS = {
  telecom: {
    type: 'usage_based',
    conditions: [
      { metric: 'inactivity_period', value: 30, unit: 'days' },
      { metric: 'usage_drop', value: 80, unit: 'percent' },
      { metric: 'bill_payment_delay', value: 45, unit: 'days' }
    ],
    metrics: ['Kullanım Süresi', 'ARPU', 'NPS Skoru', 'Destek Talepleri']
  },
  banking: {
    type: 'transaction_based',
    conditions: [
      { metric: 'account_balance', value: 100, unit: 'TL' },
      { metric: 'transaction_frequency', value: 60, unit: 'days' },
      { metric: 'product_usage', value: 1, unit: 'count' }
    ],
    metrics: ['Hesap Bakiyesi', 'İşlem Hacmi', 'Ürün Sayısı', 'Kredi Skoru']
  },
  // ...diğer sektörler için tanımlar...
};

// Churn analizi için örnek veri
const mockChurnData = {
  // Son 12 aylık churn oranı trendi
  monthlyChurnRate: [
    { month: 'Oca', churnRate: 3.2, acquisitionRate: 5.1 },
    { month: 'Şub', churnRate: 3.4, acquisitionRate: 4.8 },
    { month: 'Mar', churnRate: 3.1, acquisitionRate: 5.0 },
    { month: 'Nis', churnRate: 3.5, acquisitionRate: 4.7 },
    { month: 'May', churnRate: 3.8, acquisitionRate: 4.5 },
    { month: 'Haz', churnRate: 3.6, acquisitionRate: 4.4 },
    { month: 'Tem', churnRate: 3.9, acquisitionRate: 4.3 },
    { month: 'Ağu', churnRate: 4.1, acquisitionRate: 4.2 },
    { month: 'Eyl', churnRate: 3.8, acquisitionRate: 4.5 },
    { month: 'Eki', churnRate: 3.7, acquisitionRate: 4.6 },
    { month: 'Kas', churnRate: 3.5, acquisitionRate: 4.8 },
    { month: 'Ara', churnRate: 3.3, acquisitionRate: 5.0 }
  ],

  // Sektörlere göre churn oranları
  sectorChurnRate: {
    telecom: 4.2,
    banking: 2.8,
    ecommerce: 5.1,
    saas: 3.7,
    insurance: 3.2
  },

  // Müşteri segmentlerine göre churn oranları
  segmentChurnRate: [
    { name: 'Yeni Müşteriler', value: 5.7 },
    { name: 'Aktif Müşteriler', value: 2.3 },
    { name: 'Risk Altındaki', value: 15.2 },
    { name: 'Uzun Vadeli', value: 1.8 }
  ],

  // Churn nedenleri
  churnReasons: [
    { name: 'Fiyatlandırma', value: 35 },
    { name: 'Müşteri Desteği', value: 22 },
    { name: 'Rakip Teklif', value: 18 },
    { name: 'Kullanım Zorluğu', value: 12 },
    { name: 'Özellik Eksikliği', value: 8 },
    { name: 'Diğer', value: 5 }
  ],

  // Churn riski yüksek müşteriler
  highRiskCustomers: [
    { id: 1, name: 'Ahmet Yılmaz', risk: 92, segment: 'Kurumsal', usage: 'Düşük', tenure: 8, value: 4500 },
    { id: 2, name: 'Ayşe Kaya', risk: 89, segment: 'KOBİ', usage: 'Düşen', tenure: 11, value: 2800 },
    { id: 3, name: 'Mehmet Demir', risk: 87, segment: 'Bireysel', usage: 'Düşük', tenure: 6, value: 1200 },
    { id: 4, name: 'Zeynep Avcı', risk: 85, segment: 'Kurumsal', usage: 'Düşen', tenure: 14, value: 6700 },
    { id: 5, name: 'Ali Öztürk', risk: 84, segment: 'KOBİ', usage: 'Düşük', tenure: 9, value: 3200 }
  ],

  // Müşteri yaşam döngüsü segmenti
  customerLifecycle: [
    { name: 'Yeni Müşteriler', value: 28 },
    { name: 'Büyüyen', value: 15 },
    { name: 'Olgun', value: 32 },
    { name: 'Azalan', value: 16 },
    { name: 'Risk Altında', value: 9 }
  ],

  // Churn risk faktörleri ağırlıkları
  riskFactors: [
    { factor: 'Kullanım Düşüşü', weight: 35 },
    { factor: 'Ödeme Gecikmeleri', weight: 20 },
    { factor: 'Düşük Memnuniyet', weight: 18 },
    { factor: 'Destek Aramaları', weight: 12 },
    { factor: 'Düşük Etkileşim', weight: 10 },
    { factor: 'Rakip Kullanımı', weight: 5 }
  ],

  // Müşteri değeri ve churn riski korelasyonu
  valueLtvRisk: [
    { id: 1, customerValue: 1200, riskScore: 30, segment: 'Bireysel', size: 50 },
    { id: 2, customerValue: 4500, riskScore: 12, segment: 'Kurumsal', size: 100 },
    { id: 3, customerValue: 800, riskScore: 85, segment: 'Bireysel', size: 50 },
    { id: 4, customerValue: 3200, riskScore: 45, segment: 'KOBİ', size: 75 },
    { id: 5, customerValue: 6700, riskScore: 28, segment: 'Kurumsal', size: 120 },
    { id: 6, customerValue: 1500, riskScore: 72, segment: 'Bireysel', size: 50 },
    { id: 7, customerValue: 5200, riskScore: 18, segment: 'Kurumsal', size: 110 },
    { id: 8, customerValue: 900, riskScore: 92, segment: 'Bireysel', size: 50 },
    { id: 9, customerValue: 2800, riskScore: 63, segment: 'KOBİ', size: 70 },
    { id: 10, customerValue: 4100, riskScore: 42, segment: 'KOBİ', size: 85 },
    { id: 11, customerValue: 7500, riskScore: 15, segment: 'Kurumsal', size: 130 },
    { id: 12, customerValue: 1100, riskScore: 79, segment: 'Bireysel', size: 50 }
  ],

  // Müşteri segmentlerine göre özellikler
  customerFeatures: [
    { 
      subject: 'Etkileşim', 
      Bireysel: 75, 
      KOBİ: 82, 
      Kurumsal: 90 
    },
    { 
      subject: 'Sadakat', 
      Bireysel: 60, 
      KOBİ: 75, 
      Kurumsal: 85 
    },
    { 
      subject: 'Memnuniyet', 
      Bireysel: 65, 
      KOBİ: 70, 
      Kurumsal: 80 
    },
    { 
      subject: 'Hizmet Kullanımı', 
      Bireysel: 50, 
      KOBİ: 80, 
      Kurumsal: 95 
    },
    { 
      subject: 'Destek Talebi', 
      Bireysel: 30, 
      KOBİ: 50, 
      Kurumsal: 70 
    }
  ],

  // Müdahale kampanyalarının etkinliği
  interventionEffectiveness: [
    { type: "İndirim Teklifi", success: 65, failure: 35 },
    { type: "Özel Müşteri Desteği", success: 78, failure: 22 },
    { type: "Ürün Eğitimi", success: 45, failure: 55 },
    { type: "Ücretsiz Ek Özellik", success: 72, failure: 28 },
    { type: "Uzun Dönem Sözleşme", success: 58, failure: 42 }
  ],

  // Model performans metrikleri
  modelPerformance: {
    accuracy: 86,
    precision: 82,
    recall: 79,
    f1Score: 80.5,
    auc: 0.88
  },

  // Churn yüzdesi ve önleme yüzdesi
  summary: {
    predictedChurn: 12.5,
    preventionRate: 72,
    savingsAmount: 1850000,
    roi: 420
  }
};

// Farklı churn tahmin modelleri
const predictionModels = {
  "Lojistik Regresyon": { accuracy: 82.5, precision: 79.3, recall: 76.2, f1Score: 77.7, complexity: "Düşük", trainingTime: "1m 32s" },
  "Rastgele Orman": { accuracy: 86.8, precision: 84.7, recall: 81.9, f1Score: 83.3, complexity: "Orta", trainingTime: "3m 45s" },
  "Gradyan Artırma": { accuracy: 88.2, precision: 85.9, recall: 82.6, f1Score: 84.2, complexity: "Yüksek", trainingTime: "7m 18s" },
  "Derin Öğrenme": { accuracy: 89.5, precision: 87.2, recall: 84.8, f1Score: 86.0, complexity: "Çok Yüksek", trainingTime: "15m 40s" },
  "SVM": { accuracy: 84.3, precision: 81.6, recall: 78.5, f1Score: 80.0, complexity: "Orta", trainingTime: "4m 12s" }
};

// Müşteri yaşam döngüsü aşamaları - Kullanılmadığı için kaldırıldı veya kullanılması gerekiyorsa, aşağıdan yorum işaretini kaldırın
/* const lifecycleStages = [
  { key: 'new', label: 'Yeni Müşteriler', color: '#2196f3', icon: <PersonIcon /> },
  { key: 'growing', label: 'Büyüyen', color: '#4caf50', icon: <TrendingUpIcon /> },
  { key: 'mature', label: 'Olgun', color: '#ff9800', icon: <CheckCircleIcon /> },
  { key: 'declining', label: 'Azalan', color: '#f44336', icon: <TrendingDownIcon /> },
  { key: 'atrisk', label: 'Risk Altında', color: '#d32f2f', icon: <WarningIcon /> }
]; */

// Churn'e etki eden ana featurelar
const CHURN_FEATURES = {
  customerSupport: {
    title: 'Müşteri Desteği Kalitesi',
    description: 'Destek taleplerine yanıt süresi ve müşteri memnuniyeti',
    impact: 0.15 // %15 etki
  },
  productUsage: {
    title: 'Ürün Kullanım Oranı',
    description: 'Müşterinin ürünü aktif kullanım seviyesi',
    impact: 0.25 // %25 etki
  },
  billing: {
    title: 'Fatura/Ödeme Davranışı',
    description: 'Ödeme gecikmeleri ve fatura tutarındaki değişimler',
    impact: 0.20 // %20 etki
  },
  competitorActivity: {
    title: 'Rakip Aktiviteleri',
    description: 'Rakiplerin kampanya ve teklifleri',
    impact: 0.15 // %15 etki
  },
  serviceQuality: {
    title: 'Hizmet Kalitesi',
    description: 'Servis kesintileri ve performans sorunları',
    impact: 0.25 // %25 etki
  }
};

const ChurnAnalizi = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedModel, setSelectedModel] = useState("Rastgele Orman");
  // Kullanılmayan state'leri kaldırdık
  // const [timeRange, setTimeRange] = useState('12month');
  const [customerSegment, setCustomerSegment] = useState('all');
  // const [interventionType, setInterventionType] = useState('all');
  const [riskThreshold, setRiskThreshold] = useState(70);
  const [isLoadingPredictions, setIsLoadingPredictions] = useState(false);
  const [customerDetailOpen, setCustomerDetailOpen] = useState(false);
  // Kullanılmayan state, yorum satırına alabiliriz veya aşağıdaki gibi kullanabiliriz
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [params, setParams] = useState({
    sector: 'telecom',
    segment: 'all',
    dateRange: 'last12months'
  });
  const [timeRange, setTimeRange] = useState('6m');
  const [futurePredictionRange, setFuturePredictionRange] = useState('6m');
  const [showChurnDefinition, setShowChurnDefinition] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [churnData, setChurnData] = useState({ historical: [], predictions: [] });
  const [loading, setLoading] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState({
    customerSupport: true,
    productUsage: true,
    billing: true,
    competitorActivity: false,
    serviceQuality: true
  });

  // Tab değiştirme fonksiyonu
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Parametre değişim fonksiyonu
  const handleParamsChange = (newParams) => {
    setParams({ ...params, ...newParams });
  };

  // Müşteri detayı kapatma fonksiyonu
  const handleCustomerDetailClose = () => {
    setCustomerDetailOpen(false);
  };

  // Risk eşiğini değiştirme fonksiyonu
  const handleRiskThresholdChange = (event, newValue) => {
    setRiskThreshold(newValue);
  };

  // Model seçimi değiştirme
  const handleModelChange = (event) => {
    setSelectedModel(event.target.value);
  };

  // Tahmin yenileme fonksiyonu
  const handleRefreshPredictions = () => {
    setIsLoadingPredictions(true);
    // Simüle edilmiş yükleme süresi
    setTimeout(() => {
      setIsLoadingPredictions(false);
    }, 2000);
  };

  // Sektöre göre churn oranını getir
  const getChurnRateForSector = () => {
    return mockChurnData.sectorChurnRate[params.sector] || 3.5;
  };

  // Churn nedenleri doughnut grafiği için veri 
  const churnReasonData = mockChurnData.churnReasons;

  // Segment bazında churn oranları
  const segmentChurnData = mockChurnData.segmentChurnRate;

  // Müşteri segmentine göre radar verisi
  const radarData = mockChurnData.customerFeatures;
  
  // Müşteri değeri ve risk matrisi
  const scatterData = mockChurnData.valueLtvRisk;

  // Intervention effectiveness data
  const interventionData = mockChurnData.interventionEffectiveness;

  // Müşteri yaşam döngüsü verisi
  const lifecycleData = mockChurnData.customerLifecycle;

  // Bu fonksiyonu ekleyebiliriz
  const handleCustomerDetailOpen = (customer) => {
    setSelectedCustomer(customer);
    setCustomerDetailOpen(true);
  };

  // Seçili zaman aralığına göre veri getirme
  const getChurnDataForTimeRange = (range) => {
    // Gerçek bir API çağrısı simülasyonu
    return new Promise((resolve) => {
      setTimeout(() => {
        // Gerçekçi veri üretimi
        const months = range === '3m' ? 3 : range === '6m' ? 6 : range === '1y' ? 12 : 24;
        const data = Array.from({ length: months }, (_, i) => {
          const date = new Date();
          date.setMonth(date.getMonth() - (months - i - 1));
          
          // Mevsimsel etkileri ekleyelim
          const seasonalFactor = 1 + 0.2 * Math.sin((i / months) * Math.PI * 2);
          
          // Genel trend (hafif artış)
          const trendFactor = 1 + (i / months) * 0.1;
          
          // Temel churn oranı (sektöre göre değişir)
          const baseChurnRate = params.sector === 'telecom' ? 2.5 : 
                              params.sector === 'banking' ? 1.8 : 
                              params.sector === 'saas' ? 3.2 : 2.0;
          
          return {
            date: date.toLocaleDateString('tr-TR', { month: 'short', year: 'numeric' }),
            churnRate: +(baseChurnRate * seasonalFactor * trendFactor).toFixed(2),
            customerCount: Math.floor(10000 - (i * 100 * Math.random())),
            revenue: Math.floor(1000000 - (i * 1000 * Math.random())),
          };
        });
        
        resolve(data);
      }, 1000);
    });
  };

  // Gelecek tahminleri üretme
  const generateFuturePredictions = (historicalData, range) => {
    const months = range === '3m' ? 3 : range === '6m' ? 6 : 12;
    const lastValue = historicalData[historicalData.length - 1];
    
    // Son 6 aydaki trend
    const trend = (historicalData[historicalData.length - 1].churnRate - 
                  historicalData[historicalData.length - 6].churnRate) / 6;
    
    return Array.from({ length: months }, (_, i) => {
      const date = new Date(lastValue.date);
      date.setMonth(date.getMonth() + i + 1);
      
      // Trend bazlı tahmin
      const predictedChurnRate = +(lastValue.churnRate + (trend * (i + 1))).toFixed(2);
      
      // Tahmin aralığı (güven aralığı)
      const confidence = 0.95 - (i * 0.05); // Zaman ilerledikçe güven azalır
      const margin = predictedChurnRate * (1 - confidence);
      
      return {
        date: date.toLocaleDateString('tr-TR', { month: 'short', year: 'numeric' }),
        predictedChurnRate,
        upperBound: +(predictedChurnRate + margin).toFixed(2),
        lowerBound: +(predictedChurnRate - margin).toFixed(2),
        confidence: +(confidence * 100).toFixed(1)
      };
    });
  };

  // Parametre değişikliklerini izle
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const historicalData = await getChurnDataForTimeRange(timeRange);
        const futurePredictions = generateFuturePredictions(historicalData, futurePredictionRange);
        
        setChurnData({ historical: historicalData, predictions: futurePredictions });
      } catch (error) {
        console.error('Veri yükleme hatası:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [timeRange, futurePredictionRange, params.sector]);

  // Feature seçimlerini işle
  const handleFeatureToggle = (feature) => {
    setSelectedFeatures(prev => ({
      ...prev,
      [feature]: !prev[feature]
    }));
  };

  // Seçili featureların toplam etkisini hesapla
  const calculateFeatureEffect = (baseValue) => {
    let effect = 0;
    Object.entries(selectedFeatures).forEach(([feature, isSelected]) => {
      if (isSelected) {
        effect += CHURN_FEATURES[feature].impact;
      }
    });
    return baseValue * (1 + effect);
  };

  // Churn tahminlerini feature etkilerine göre güncelle
  const getAdjustedPredictions = (historicalData) => {
    const lastActual = historicalData[historicalData.length - 1].churnRate;
    const predictions = [];
    
    for (let i = 1; i <= 6; i++) {
      // Temel tahmin (trend bazlı)
      const basePrediction = lastActual * (1 + (i * 0.05));
      
      // Feature etkilerini uygula
      const adjustedPrediction = calculateFeatureEffect(basePrediction);
      
      // Güven aralığı (zaman ilerledikçe artan belirsizlik)
      const uncertainty = 0.05 * i;
      
      predictions.push({
        period: `+${i} Ay`,
        predictedChurnRate: adjustedPrediction,
        upperBound: adjustedPrediction * (1 + uncertainty),
        lowerBound: adjustedPrediction * (1 - uncertainty),
        isProjection: true
      });
    }
    
    return predictions;
  };

  // Ana trend grafiğini güncelle
  const renderTrendChart = () => (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" color="primary.main" gutterBottom>
              Müşteri Kaybı (Churn) Trend Analizi ve Tahminleri
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Aktif müşteri portföyünüzden kaybedilen müşteri oranlarının zaman içindeki değişimi ve gelecek dönem tahminleri. 
              Yüzdeler, ilgili dönemdeki toplam aktif müşteri sayısına oranla kaybedilen müşteri oranını gösterir.
            </Typography>
            <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
              * Örnek: %3.5 değeri, her 100 aktif müşteriden 3.5'inin kaybedildiğini gösterir.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#f44336', borderRadius: '50%', mr: 1 }} />
                <Typography variant="caption">Gerçekleşen Kayıp Oranı</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#2196f3', borderRadius: '50%', mr: 1 }} />
                <Typography variant="caption">Tahmini Kayıp Oranı</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 12, height: 12, bgcolor: 'rgba(33, 150, 243, 0.1)', mr: 1 }} />
                <Typography variant="caption">Tahmin Güven Aralığı</Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={9}>
          <Box sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={[...mockChurnData.monthlyChurnRate, ...getAdjustedPredictions(mockChurnData.monthlyChurnRate)]}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis domain={[0, 'auto']} />
                <RechartsTooltip 
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const data = payload[0].payload;
                    
                    return (
                      <Paper sx={{ p: 1.5, boxShadow: 2 }}>
                        <Typography variant="subtitle2">
                          {data.month || data.period}
                        </Typography>
                        {data.isProjection ? (
                          <>
                            <Typography variant="body2">
                              Tahmin: {data.predictedChurnRate.toFixed(2)}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Aralık: {data.lowerBound.toFixed(2)}% - {data.upperBound.toFixed(2)}%
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2">
                            Gerçekleşen: {data.churnRate}%
                          </Typography>
                        )}
                      </Paper>
                    );
                  }}
                />
                <Legend />
                
                {/* Gerçekleşen değerler */}
                <Line
                  type="monotone"
                  dataKey="churnRate"
                  stroke="#f44336"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Gerçekleşen"
                />
                
                {/* Tahminler */}
                <Line
                  type="monotone"
                  dataKey="predictedChurnRate"
                  stroke="#2196f3"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ r: 3 }}
                  name="Tahmin"
                />
                
                {/* Güven aralığı */}
                <Area
                  type="monotone"
                  dataKey="upperBound"
                  stroke="none"
                  fill="#2196f3"
                  fillOpacity={0.1}
                />
                <Area
                  type="monotone"
                  dataKey="lowerBound"
                  stroke="none"
                  fill="#2196f3"
                  fillOpacity={0.1}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
        
        {/* Feature seçimleri */}
        <Grid item xs={12} md={3}>
          <Typography variant="subtitle1" gutterBottom>
            Tahmin Faktörleri
          </Typography>
          <Stack spacing={2}>
            {Object.entries(CHURN_FEATURES).map(([key, feature]) => (
              <Paper
                key={key}
                sx={{
                  p: 1.5,
                  bgcolor: selectedFeatures[key] ? 'primary.lighter' : 'background.paper',
                  border: 1,
                  borderColor: selectedFeatures[key] ? 'primary.main' : 'divider'
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedFeatures[key]}
                      onChange={() => handleFeatureToggle(key)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2">
                        {feature.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Etki: {(feature.impact * 100).toFixed(0)}%
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }} className="slide-in-left">
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Churn Analizi ve Tahmini
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Müşteri kaybı riskini önceden tespit edin, risk altındaki müşterileri belirleyin ve kaybı önleyici aksiyonlar alın.
        </Typography>
        <Divider sx={{ my: 3 }} />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          {/* Parametre kontrolleri */}
          <Grid item xs={12} md={9}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" component="h2">Analiz Parametreleri</Typography>
                
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    startIcon={<TuneIcon />}
                    size="small"
                  >
                    Filtreler
                  </Button>
                  
                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefreshPredictions}
                    disabled={isLoadingPredictions}
                    size="small"
                  >
                    {isLoadingPredictions ? 'Yükleniyor...' : 'Tahminleri Yenile'}
                  </Button>
                </Box>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sektör</InputLabel>
                    <Select
                      value={params.sector}
                      label="Sektör"
                      onChange={(e) => handleParamsChange({ sector: e.target.value })}
                    >
                      <MenuItem value="telecom">Telekomünikasyon</MenuItem>
                      <MenuItem value="banking">Bankacılık</MenuItem>
                      <MenuItem value="ecommerce">E-Ticaret</MenuItem>
                      <MenuItem value="saas">SaaS</MenuItem>
                      <MenuItem value="insurance">Sigorta</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tahmin Modeli</InputLabel>
                    <Select
                      value={selectedModel}
                      label="Tahmin Modeli"
                      onChange={handleModelChange}
                    >
                      {Object.keys(predictionModels).map((model) => (
                        <MenuItem key={model} value={model}>{model}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Müşteri Segmenti</InputLabel>
                    <Select
                      value={customerSegment}
                      label="Müşteri Segmenti"
                      onChange={(e) => setCustomerSegment(e.target.value)}
                    >
                      <MenuItem value="all">Tüm Segmentler</MenuItem>
                      <MenuItem value="individual">Bireysel</MenuItem>
                      <MenuItem value="smb">KOBİ</MenuItem>
                      <MenuItem value="corporate">Kurumsal</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>                <Grid item xs={12} md={4}>                  <FormControl fullWidth size="small">                    <InputLabel>Analiz Dönemi</InputLabel>                    <Select                      value={timeRange}                      label="Analiz Dönemi"                      onChange={(e) => setTimeRange(e.target.value)}                    >                      {TIME_RANGES.map(range => (                        <MenuItem key={range.value} value={range.value}>                          {range.label}                        </MenuItem>                      ))}                    </Select>                  </FormControl>                </Grid>                {/* Tahmin Dönemi Seçimi */}                <Grid item xs={12} md={4}>                  <FormControl fullWidth size="small">                    <InputLabel>Tahmin Dönemi</InputLabel>                    <Select                      value={futurePredictionRange}                      label="Tahmin Dönemi"                      onChange={(e) => setFuturePredictionRange(e.target.value)}                    >                      <MenuItem value="3m">3 Ay İleri</MenuItem>                      <MenuItem value="6m">6 Ay İleri</MenuItem>                      <MenuItem value="1y">1 Yıl İleri</MenuItem>                    </Select>                  </FormControl>                </Grid>              </Grid>                            <Box sx={{ mt: 3 }}>                <Typography variant="body2" gutterBottom>                  Risk Eşiği: {riskThreshold}%                </Typography>                <Slider                  value={riskThreshold}                  onChange={handleRiskThresholdChange}                  valueLabelDisplay="auto"                  step={5}                  marks                  min={40}                  max={90}                />              </Box>            </Paper>          </Grid>                    {/* Özet metrikler */}          <Grid item xs={12} md={3}>            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>              <Typography variant="h6" component="h2" gutterBottom>Özet Metrikler</Typography>              <Box sx={{ textAlign: 'center', py: 2 }}>                <Typography variant="h3" color="error" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>                  <TrendingDownIcon color="error" sx={{ mr: 1 }} />                  {getChurnRateForSector()}%                </Typography>                <Typography variant="body2" color="text.secondary">                  Aylık Churn Oranı                </Typography>              </Box>                            <Divider sx={{ my: 2 }} />                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>                <Typography variant="body2">Tahmin Doğruluğu:</Typography>                <Typography variant="body1" fontWeight="bold" color="primary">                  {predictionModels[selectedModel].accuracy}%                </Typography>              </Box>                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>                <Typography variant="body2">Önleme Başarısı:</Typography>                <Typography variant="body1" fontWeight="bold" color="success.main">                  {mockChurnData.summary.preventionRate}%                </Typography>              </Box>            </Paper>          </Grid>        </Grid>      </Box>      <Tabs        value={activeTab}        onChange={handleTabChange}        variant="fullWidth"        sx={{ mb: 3 }}        className="fade-in"      >        <Tab label="Risk Analizi" icon={<WarningIcon />} iconPosition="start" />        <Tab label="Segment Analizi" icon={<PeopleIcon />} iconPosition="start" />        <Tab label="Önleme Stratejileri" icon={<TrendingUpIcon />} iconPosition="start" />      </Tabs>      {/* Risk Analizi tab içeriği */}      <Box className="fade-in" sx={{ display: activeTab === 0 ? 'block' : 'none' }}>        <Grid container spacing={4}>          {/* Ana trend grafiği */}          <Grid item xs={12}>            {renderTrendChart()}          </Grid>                    {/* Churn nedenleri ve risk dağılımı */}          <Grid item xs={12} md={6}>            <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>              <Typography variant="h6" gutterBottom>Churn Nedenleri</Typography>              <Box sx={{ height: 300 }}>                <ResponsiveContainer width="100%" height="100%">                  <PieChart>                    <Pie                      data={churnReasonData}                      innerRadius={60}                      outerRadius={80}                      paddingAngle={2}                      dataKey="value"                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}                    >                      {churnReasonData.map((entry, index) => (                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />                      ))}                    </Pie>                    <RechartsTooltip formatter={(value) => `${value}%`} />                    <Legend />                  </PieChart>                </ResponsiveContainer>              </Box>            </Paper>          </Grid>          {/* Değer-Risk Matrisi */}          <Grid item xs={12} md={6}>            <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>              <Typography variant="h6" gutterBottom>Müşteri Değeri ve Churn Riski Matrisi</Typography>              <Box sx={{ height: 300 }}>                <ResponsiveContainer width="100%" height="100%">                  <ScatterChart                    margin={{ top: 20, right: 20, bottom: 20, left: 20 }}                  >                    <CartesianGrid />                    <XAxis                       type="number"                       dataKey="customerValue"                       name="Müşteri Değeri"                       unit="₺"                       label={{ value: 'Müşteri Değeri (₺)', position: 'insideBottom', offset: -5 }}                     />                    <YAxis                       type="number"                       dataKey="riskScore"                       name="Risk Skoru"                       unit="%"                       label={{ value: 'Risk Skoru (%)', angle: -90, position: 'insideLeft', offset: -5 }}                     />                    <ZAxis type="number" dataKey="size" range={[50, 200]} />                    <RechartsTooltip cursor={{ strokeDasharray: '3 3' }} />                    <Legend />                    <Scatter                       name="Müşteriler"                       data={scatterData}                       fill="#8884d8"                       shape="circle"                     />                  </ScatterChart>                </ResponsiveContainer>              </Box>            </Paper>          </Grid>        </Grid>      </Box>

      {/* Segment Analizi tab içeriği */}
      <Box className="fade-in" sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
        <Grid container spacing={4}>
          {/* Segment bazında churn oranları */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Segment Bazında Churn Oranları</Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={segmentChurnData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="value" name="Churn Oranı (%)" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              <Button 
                size="small" 
                variant="outlined"
                onClick={() => {
                  // Örnek bir müşteriyi seç
                  const sampleCustomer = mockChurnData.highRiskCustomers[0];
                  handleCustomerDetailOpen(sampleCustomer);
                }}
                sx={{ mt: 2 }}
              >
                Müşteri Detayı
              </Button>
            </Paper>
          </Grid>

          {/* Müşteri segmentine göre radar grafiği */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Segment Bazlı Müşteri Metrikleri</Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { segment: 'Bireysel', memnuniyet: 65, sadakat: 60, aktivite: 50 },
                      { segment: 'KOBİ', memnuniyet: 70, sadakat: 75, aktivite: 80 },
                      { segment: 'Kurumsal', memnuniyet: 80, sadakat: 85, aktivite: 95 }
                    ]}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} unit="%" />
                    <YAxis dataKey="segment" type="category" />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="memnuniyet" name="Müşteri Memnuniyeti" fill="#8884d8" />
                    <Bar dataKey="sadakat" name="Sadakat Skoru" fill="#82ca9d" />
                    <Bar dataKey="aktivite" name="Aktivite Oranı" fill="#ffc658" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Önleme Stratejileri tab içeriği */}
      <Box className="fade-in" sx={{ display: activeTab === 2 ? 'block' : 'none' }}>
        <Grid container spacing={4}>
          {/* Müdahale kampanyalarının etkinliği */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Müdahale Kampanyalarının Etkinliği</Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={interventionData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="type" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="success" name="Başarı Oranı (%)" fill="#4caf50" />
                    <Bar dataKey="failure" name="Başarısızlık Oranı (%)" fill="#f44336" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Müşteri yaşam döngüsü */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Risk Grubu Dağılımı ve Tahmini Kayıp</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Müşterilerin risk seviyelerine göre dağılımı ve potansiyel kayıp öngörüsü
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Düşük Risk (<30%)', value: 65, fill: '#4caf50' },
                        { name: 'Orta Risk (30-70%)', value: 25, fill: '#ff9800' },
                        { name: 'Yüksek Risk (>70%)', value: 10, fill: '#f44336' }
                      ]}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, value }) => `${name}: %${value}`}
                    />
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'error.lighter', borderRadius: 1 }}>
                <Typography variant="body2" color="error.dark">
                  Tahmini Aylık Kayıp: {mockChurnData.summary.predictedChurn}% 
                  ({(mockChurnData.summary.savingsAmount/1000000).toFixed(1)} Milyon ₺)
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <CustomerDetailDialog 
        open={customerDetailOpen} 
        onClose={handleCustomerDetailClose} 
        customer={selectedCustomer} 
      />
    </Container>
  );
};

export default ChurnAnalizi;
